import Cookies from 'js-cookie';

import axios from 'axios';
import { getUserKey } from '../utils/userKey';
import {
    getCartIds,
} from '../utils/cart';
import {
    QUERY_TIMEOUT_MS,
    QUERY_POLL_TIMEOUT_MS,
    QUERY_POLL_INTERVAL_MS,
    FETCH_MAX_RETRIES,
    FETCH_RETRY_DELAY_MS,
    EXPORT_POLL_MS,
    EXPORT_MAX_POLL_MINUTES,
} from '../config';

export async function downloadPaperCleanText(fileId) {
    const response = await fetch(`${API_BASE_URL}/upload/papers/${fileId}/clean-text/`, {
        credentials: 'include',
    });
    if (!response.ok) throw new Error('Clean TXT is not available yet. Please try again after conversion.');
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement('a');
    link.href = url;
    link.download = 'cleaned.txt';
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function previewPaperMetadata({ file, doi, pmid }) {
    getUserKey();
    const data = new FormData();
    if (file) data.append('file', file);
    if (doi) data.append('doi', doi);
    if (pmid) data.append('pmid', pmid);
    const response = await fetch(`${API_BASE_URL}/upload/metadata/preview/`, {
        method: 'POST', credentials: 'include',
        headers: { 'X-CSRFToken': await getCsrfToken() }, body: data,
    });
    const body = await response.json();
    if (!response.ok) throw new Error(body.error || 'Unable to identify article.');
    return body;
}

export async function getPaperLibrary() {
    getUserKey();
    const response = await fetch(`${API_BASE_URL}/upload/library/`, { credentials: 'include' });
    if (!response.ok) throw new Error('Unable to load saved papers.');
    return response.json();
}

export async function confirmPaperMetadata(token) {
    getUserKey();
    const response = await fetch(`${API_BASE_URL}/upload/metadata/confirm/`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRFToken': await getCsrfToken() },
        body: JSON.stringify({ token }),
    });
    const body = await response.json();
    if (!response.ok) throw new Error(body.error || 'Unable to save citation.');
    return body;
}

export async function downloadLibraryText(assetId) {
    const response = await fetch(`${API_BASE_URL}/upload/library/${assetId}/clean-text/`, { credentials: 'include' });
    if (!response.ok) throw new Error('Clean TXT is not available yet.');
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement('a');
    link.href = url; link.download = 'cleaned.txt'; link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * API services for calling backend endpoints.
 * This file contains all functions to interact with the backend API.
 * Any interaction with the backend should be done through this file.
 * When response is not ok, functions generally print out message in console and throw an error with new message.
 * Where necessary, error message is checked for specific keywords to provide more user-friendly error messages.
 */

// Dynamically set the API base URL based on the environment. For development, it uses the environment variable REACT_APP_API_BASE_URL or defaults to 'http://localhost:8000', assuming you are running both frontend and backend on the same localhost.
// For production, it uses the relative path '/api' which is a reverse proxy to the backend server configured in Nginx.
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'
export { API_BASE_URL };

export async function getPublicationByKey(key) {
    const response = await fetch(
        `${API_BASE_URL}/upload/publications/by-key/${encodeURIComponent(key)}/`,
        { credentials: 'include' }
    );
    if (!response.ok) {
        throw new Error(response.status === 404
            ? 'Publication not found.'
            : `Unable to resolve publication (${response.status}).`);
    }
    return response.json();
}

/**
 * Tell the backend the user has left this graph — drops the cached load_params
 * payload so the next user/session pays for fresh download. Best-effort; uses
 * sendBeacon when available so it survives unload.
 */
export function releaseGraph(id) {
    if (id == null) return;
    const url = `${API_BASE_URL}/search/release/${id}/`;
    try {
        if (navigator.sendBeacon) {
            navigator.sendBeacon(url, new Blob([], { type: 'application/json' }));
            return;
        }
    } catch (e) { /* fall through to fetch */ }
    fetch(url, { method: 'POST', credentials: 'include', keepalive: true })
        .catch(() => { /* best-effort */ });
}

function _xhrPutAzure(url, file, onProgress) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', url);
        xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
        xhr.setRequestHeader('Content-Type', 'application/zip');
        if (onProgress) {
            xhr.upload.onprogress = (ev) => {
                if (ev.lengthComputable) onProgress(ev.loaded, ev.total);
            };
        }
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else reject(new Error(`Upload to Azure failed (${xhr.status}): ${xhr.responseText || ''}`));
        };
        xhr.onerror = () => reject(new Error('Network error during upload.'));
        xhr.send(file);
    });
}

/**
 * Direct-to-Azure import. 3 steps: init → PUT zip to SAS URL → commit.
 * Commit returns 202; the actual restore runs in a background thread on the
 * backend and the resulting GraphModel + PipelineJob (status=running) appear
 * in the Jobs list. The frontend doesn't wait for the restore to finish.
 * Every import receives a new graph identity, even when the archive came from
 * a graph that already exists in this deployment.
 */
export async function importGraphDirect(file, onProgress) {
    const csrfToken = await getCsrfToken();
    // 1. Create a fresh staged upload for every import attempt.
    const initRes = await fetch(`${API_BASE_URL}/index/import-graph/init/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-CSRFToken': csrfToken, 'Content-Type': 'application/json' },
        body: '{}',
    });
    if (!initRes.ok) {
        let msg = `Import init failed (${initRes.status})`;
        try { const body = await initRes.json(); msg = body.error || msg; } catch (e) { /* keep */ }
        throw new Error(msg);
    }
    const initBody = await initRes.json();
    const importId = initBody.import_id;
    // 2. PUT zip directly to Azure
    await _xhrPutAzure(initBody.sas_url, file, onProgress);
    // 3. commit
    const commitRes = await fetch(`${API_BASE_URL}/index/import-graph/commit/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-CSRFToken': csrfToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({ import_id: importId }),
    });
    if (commitRes.status >= 200 && commitRes.status < 300) {
        const body = await commitRes.json();
        return { ...body, _importId: importId };
    }
    let msg = `Import failed (${commitRes.status})`;
    let body = null;
    try { body = await commitRes.json(); msg = body.error || msg; } catch (e) { /* keep */ }
    const err = new Error(msg);
    err.status = commitRes.status;
    err.code = body && body.code;
    err.body = body;
    throw err;
}

/**
 * Direct-to-Azure export. Kicks off a background build; polls until ready;
 * resolves to { downloadUrl, filename } for the caller to navigate to.
 * The caller decides UX (window.location, anchor click, etc.).
 */
export async function exportGraphDirect(graphId, { onStatus, pollMs = EXPORT_POLL_MS, maxPollMinutes = EXPORT_MAX_POLL_MINUTES } = {}) {
    const csrfToken = await getCsrfToken();
    const initRes = await fetch(`${API_BASE_URL}/search/export-graph/${graphId}/init/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-CSRFToken': csrfToken, 'Content-Type': 'application/json' },
        body: '{}',
    });
    if (!initRes.ok) {
        let msg = `Export init failed (${initRes.status})`;
        try { const b = await initRes.json(); msg = b.error || msg; } catch (e) { /* keep */ }
        throw new Error(msg);
    }
    const { export_id } = await initRes.json();
    if (onStatus) onStatus({ status: 'building' });

    const deadline = Date.now() + maxPollMinutes * 60 * 1000;
    while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, pollMs));
        const statusRes = await fetch(
            `${API_BASE_URL}/search/export-graph/${graphId}/status/?export_id=${encodeURIComponent(export_id)}`,
            { credentials: 'include' }
        );
        if (!statusRes.ok) {
            let msg = `Export status check failed (${statusRes.status})`;
            try { const b = await statusRes.json(); msg = b.error || msg; } catch (e) { /* keep */ }
            throw new Error(msg);
        }
        const body = await statusRes.json();
        if (onStatus) onStatus(body);
        if (body.status === 'ready') {
            return { downloadUrl: body.download_url, filename: body.filename, expiresAt: body.expires_at };
        }
        if (body.status === 'failed' || body.status === 'expired') {
            throw new Error(body.error || `Export ${body.status}`);
        }
    }
    throw new Error('Export timed out');
}

async function _fetchWithRetry(url, options, retries = FETCH_MAX_RETRIES) {
  const res = await fetch(url, options);

  if (res.status === 429 && retries > 0) {
    const retryAfter = res.headers.get('Retry-After');
    const delay = retryAfter ? parseInt(retryAfter) * 1000 : FETCH_RETRY_DELAY_MS;
    await new Promise(r => setTimeout(r, delay));
    return _fetchWithRetry(url, options, retries - 1);
  }

  return res;
}

/**
 * Fetches the CSRF token from the backend. To be run before any POST request to ensure CSRF protection. May not be required in production (same-origin).
 * @returns {Promise<string>} - A promise that resolves to the CSRF token.
 */
export async function getCsrfToken() {
    try {
        await axios.get(`${API_BASE_URL}/csrf/`, { withCredentials: true });
        const csrftoken = Cookies.get('csrftoken');

        return csrftoken;
    } catch (error) {
        console.error('Error fetching CSRF token:', error);
        throw new TypeError('Failed to fetch CSRF token');
    }
}

export async function getCurrentUser() {
    const response = await fetch(`${API_BASE_URL}/auth/session/`, {
        credentials: 'include',
    });
    if (!response.ok) throw new Error('Unable to check login status.');
    return await response.json();
}

export async function loginUser(username, password) {
    const csrfToken = await getCsrfToken();
    const response = await fetch(`${API_BASE_URL}/auth/login/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
        },
        body: JSON.stringify({ username, password }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.error || 'Login failed.');
    return body;
}

export async function logoutUser() {
    const csrfToken = await getCsrfToken();
    const response = await fetch(`${API_BASE_URL}/auth/logout/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-CSRFToken': csrfToken },
    });
    if (!response.ok) throw new Error('Logout failed.');
}

export async function changePassword(currentPassword, newPassword, confirmation) {
    const csrfToken = await getCsrfToken();
    const response = await fetch(`${API_BASE_URL}/auth/change-password/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
        },
        body: JSON.stringify({
            current_password: currentPassword,
            new_password: newPassword,
            new_password_confirmation: confirmation,
        }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.error || 'Unable to change password.');
    return body;
}

export async function submitPapers(papers, setUploadProgress, graphTitle, completionModel, chatModel, publicAccess, extractionVersion = 'v1') {
    let graph_id = 0;
    const csrfToken = await getCsrfToken();
    const user_key = getUserKey();
    // 1. start batch — send metadata (backend will create graph and return graph id)
    try {

        const formData = new FormData();
        // send metadata as JSON string (without the file)
        const papersMeta = papers.map(({ pdfFile, ...rest }) => rest);
        formData.append('papers', JSON.stringify(papersMeta));
        formData.append('user_key', user_key);
        formData.append('graph_title', graphTitle);
        formData.append('completion_model', completionModel);
        formData.append('extraction_version', extractionVersion === 'v2_stats' ? 'v2' : extractionVersion);
        formData.append('extraction_scope', extractionVersion === 'v2_stats' ? 'stats_only' : 'full');
        formData.append('chat_model', chatModel);
        formData.append('public_access', publicAccess ? 'true' : 'false');

        // 1. start batch — send metadata (backend will create graph and return graph id)
        const startRes = await fetch(`${API_BASE_URL}/upload/start/`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'X-CSRFToken': csrfToken,
            },
            body: formData,
        });

        if (!startRes.ok) {
            const errorBody = await startRes.text();

            if (errorBody.toLowerCase().includes('graph limit')) {
                throw new Error('Graph limit reached');
            }
            throw new Error('Failed to start upload batch');
        }
        const data = await startRes.json();

        graph_id = data.graph_id
    } catch (e) {
        console.error('Upload failed to initiate:', e);
        if (e?.message?.toLowerCase().includes('graph limit')) {
            throw new Error('Graph limit reached');
        }
        throw new Error('Upload failed to initiate');
    }
    //2. send papers one by one
    try{

        if (graph_id===0) {
            throw new Error('Graph ID not found');
        }

        for (let i = 0; i < papers.length; i++)  {
            if (papers[i].pdfFile) {
                setUploadProgress({
                    current: i,
                    total: papers.length,
                    });
                const formData = new FormData();
                formData.append('graph_id', graph_id);
                formData.append('file', papers[i].pdfFile);
                formData.append('display_name', papers[i].title || papers[i].pdfFile.name);
                if (papers[i].metadata_token) formData.append('metadata_token', papers[i].metadata_token);
                const fileRes = await _fetchWithRetry(`${API_BASE_URL}/upload/file/`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'X-CSRFToken': csrfToken },
                    body: formData,
                });
                if (!fileRes.ok) {
                    if (fileRes.status === 413) {
                        throw new Error(
                            `File is too large for the server upload limit: ${papers[i].title}`
                        );
                    }
                    const errorBody = await fileRes.json().catch(() => ({}));
                    throw new Error(
                        errorBody.error || `Failed to upload file: ${papers[i].title}`
                    );
                }
            }
        }
    } catch (e) {
        console.error('File upload failed:', e);
        throw new Error(e);
    }
    //3. Mark as complete
    try {

        const completeRes = await fetch(`${API_BASE_URL}/upload/complete/`, {
            method: 'POST',
            credentials: 'include',
            headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
            },
            body: JSON.stringify({ graph_id }),
        });

        if (!completeRes.ok) throw new Error('Failed to complete upload batch');
        return await completeRes.json();
    } catch (e) {
        console.error('File upload confirmation failed:', e);
        throw new Error(e);
    }
}

export async function loadCart(){
    const ids = getCartIds();
    if (ids.length === 0) return [];
    try {
        const csrf = await getCsrfToken();
        const response = await fetch(`${API_BASE_URL}/upload/publications/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-CSRFToken': csrf },
            body: JSON.stringify({ ids }),
            credentials: 'include',
        });
        if (!response.ok) {
            console.error('Error fetching cart:', response);
            throw new Error('Network response was not ok');
        }
        const pubs = await response.json();
        // Preserve localStorage insertion order; drop ids the server didn't return.
        const byId = new Map(pubs.map((p) => [p.id, p]));
        return ids.map((id) => byId.get(id)).filter(Boolean);
    } catch (error) {
        console.error('Error fetching cart:', error);
        throw new Error('Failed to fetch cart due to an error');
    }
}

/**
 * Retrieve only the graph with uuid present in the Shared URL.
 */
export async function retrieveGraph(uuid) {
    await getCsrfToken();
    try {
        const response = (await fetch(`${API_BASE_URL}/index/retrieve/${uuid}/`, {
            credentials: 'include',
        }));
        if (!response.ok) {
            throw new Error(await response.text());
        }
        const job = await response.json();



        const extracted_job = {
            id: job.id,
            uuid: job.uuid,
            title: job.title,
            user_key: job.user_key,
            status: job.status,
            progress: job.progress,
            percent_complete: job.percent_complete,
            all_workflows:job.all_workflows,
            completed_workflows: job.completed_workflows,
            failed_workflows: job.failed_workflows,
            owner_username: job.owner_username,
            owner_display_name: job.owner_display_name,
            shared_with: job.shared_with || [],
            shared_with_display_names: job.shared_with_display_names || [],
            can_manage: job.can_manage,
            completion_model: job.completion_model,
            extraction_version: job.extraction_version || 'v1',
            workflow_version: job.workflow_version || job.extraction_version || 'v1',
            chat_model: job.chat_model,
            public_access: Boolean(job.public_access),
        }
        return [extracted_job];
    } catch (e) {
        console.error('Error retrieving graph:', e);
        throw new Error('Failed to retrieve files due to an error.');
    }
}

/**
 * Fetches the output files associated with a specific ID.
 * @param {*} id - The ID of the files to fetch.
 * @returns {Promise<Array>} - A promise that resolves to an array of files.
 */
export async function getFiles(id) {
    const csrfToken = await getCsrfToken();
    try {
        const response = await fetch(`${API_BASE_URL}/search/getfiles/${id}/`, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'X-CSRFToken': csrfToken,
            },
        });
        if (!response.ok) {
            const error = await response.text();
            throw new Error(error);
        }
        const resJson = await response.json(); // [{file_name, url, mime_type}, ...]
        const files = await Promise.all(
            resJson.map(async ({ file_name, url, mime_type }) => {
                const blobRes = await fetch(url, {
                    method: 'GET',
                    mode: 'cors',
                });
                if (!blobRes.ok) {

                    throw new Error(`Blob fetch failed for ${file_name} (status ${blobRes.status})`);
                }
                const blob = await blobRes.blob();
                return new File([blob], file_name, { type: mime_type });
            })
        );

        return files;
    } catch (error) {
        console.error('Error fetching files:', error);
        throw new Error('Failed to fetch files due to an error.');
    }
}

export async function getChatHistory (graphId) {
    try {
        const response = await fetch(`${API_BASE_URL}/search/gethistory/${parseInt(graphId)}/`);
        if (!response.ok) throw new Error('Failed to fetch chat history');

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching chat history:', error);
        throw new Error('Failed to load chat history due to an error.')
    }
};

/**
 * Sends a query to the backend API.
 * @param {*} method - The method to use for the query ('global', 'local', 'drift').
 * @param {*} query - The query string to send.
 * @param {*} id - The ID of the graph.
 * @returns {Promise<Object>} - A promise that resolves to the response from the backend.
 * * @throws {Error} - Throws an error if the query fails
 * * @throws {Error} - Throws an error if the vector store is not available for local search.
 * * @throws {Error} - Throws an error if the request times out after 4 minutes.
 * * @throws {Error} - Throws an error if querying unsupported method.
 */

export async function getQueryResult(query_id) {
    const response = await fetch(`${API_BASE_URL}/search/result/${query_id}/`, {
        method: "GET",
        credentials: "include",
    });

    if (!response.ok) {
        const error = await response.text();
        throw new Error(error);
    }
    return await response.json();
}

/**
 * Shared worker for sendQuery + retryQuery. Owns the POST + poll loop + the
 * full error-translation ladder, so the two public entry points only need to
 * shape their request body and pass the right knownQueryId.
 *
 * Returns the bot response on `done`, the literal string `"cancelled."` on
 * cancellation, throws on `error`, and cancels + throws on the 4-minute poll
 * cap. The 10-minute AbortController is the hard ceiling on the entire flow.
 */
async function _runQuery({ method, graphId, body, knownQueryId, setLatestQID, setQID }) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), QUERY_TIMEOUT_MS);
    let queryId = knownQueryId || null;
    try {
        const csrfToken = await getCsrfToken();
        const response = await fetch(`${API_BASE_URL}/search/${method}/${graphId}/`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': csrfToken,
            },
            body: JSON.stringify(body),
            signal: controller.signal,
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));

            if (response.status === 409) {
                const conflict = new Error(error.error || 'You already have an ongoing query.');
                conflict.name = 'OngoingQueryError';
                conflict.ongoingQueryId = error.ongoing_query_id;
                conflict.ongoingGraphId = error.ongoing_graph_id;
                throw conflict;
            }
            if (response.status === 429) {
                const busy = new Error(error.error || 'Server is busy. Please try again later.');
                busy.name = 'ServerBusyError';
                throw busy;
            }
            throw new Error(error.error || 'Query request failed');
        }
        const json = await response.json();
        queryId = json.query_id || knownQueryId;
        if (setLatestQID) setLatestQID(queryId);
        if (setQID) setQID(queryId);

        const pollStart = Date.now();
        while (true) {
            const result = await getQueryResult(queryId);

            if (result.status === "done") {
                return result.response;
            }
            if (result.status === "cancelled") {

                if (setLatestQID) setLatestQID(null);
                return "cancelled.";
            }
            if (result.status === "error") {
                throw new Error(result.error || "Query failed");
            }
            if (Date.now() - pollStart >= QUERY_POLL_TIMEOUT_MS) {
                await cancelQuery(queryId);
                if (setLatestQID) setLatestQID(null);
                throw new Error(`Query timed out after ${QUERY_POLL_TIMEOUT_MS / 60000} minutes and was cancelled.`);
            }
            // pending / running
            await new Promise(r => setTimeout(r, QUERY_POLL_INTERVAL_MS));
        }
    } catch (error) {
        console.error('Error during query:', error);
        if (error.name === 'OngoingQueryError' || error.name === 'ServerBusyError') {
            throw error;
        }
        if (error.name === 'AbortError') {
            if (queryId) await cancelQuery(queryId);
            throw new Error(`Request timed out after ${QUERY_TIMEOUT_MS / 60000} minutes`);
        }
        if (error.message === "Search task was canceled.") {
            throw new Error(error.message);
        }
        // When no vector store file is found for the graph, ask user to switch to global search.
        if (error.message && error.message.includes('vector store')) {
            throw new Error("Vector store is required for local search but is not available. Please switch to global search.");
        }
        throw new Error('Query failed due to an error.');
    } finally {
        clearTimeout(timeoutId);
    }
}

export async function sendQuery(method, query, id, chatModel, setLatestQID, setQID) {

    if (method === 'drift') {
        const driftError = new Error('Drift method is not supported yet');
        driftError.name = 'ServerError';
        console.error(driftError);
        throw driftError;
    }
    return _runQuery({
        method, graphId: id,
        body: { graph_id: id, query, chat_model: chatModel },
        knownQueryId: null,
        setLatestQID, setQID,
    });
}

export async function retryQuery(method, query_id, graph_id, chatModel, setLatestQID) {
    return _runQuery({
        method, graphId: graph_id,
        body: { query_id, chat_model: chatModel },
        knownQueryId: query_id,
        setLatestQID, setQID: undefined,
    });
}

export async function cancelQuery(query_id) {
    try {
        const token = await getCsrfToken();
        const response = await fetch(`${API_BASE_URL}/search/cancel/${query_id}/`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'X-CSRFToken': token,
            },
        });

        if (!response.ok) {
            const error = await response.json();
            console.error('Task cannot be canceled due to Error:', error);
            throw new Error(error);
        }
        const resJson = await response.json();

        return resJson;
    } catch (error) {   
        console.error('Error canceling query:', error);
        throw new Error('Error canceling query.');
    }
}

/**
 * Starts the indexing process for a specific graph.
 * @param {*} id - The ID of the graph to index.
 * @returns {Promise<Object>} - A promise that resolves to the response from the backend. Specific format of response does not matter as long as the request is successful.
 * * @throws {Error} - Throws an error if the indexing fails.
 */
export async function startIndex(id) {
    try {
        const csrfToken = await getCsrfToken();
        const response = await fetch(`${API_BASE_URL}/index/start/${id}/`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'X-CSRFToken': csrfToken,
            },
        });
        if (response.status !== 200 && response.status !== 201) {
            const error = await response.text();
            const errorJson = JSON.parse(error);
            throw new Error(errorJson.message || 'Failed to start indexing');
        }
        const resJson = await response.json();

        return resJson;
    } catch (error) {
        console.error('Error starting index:', error);
        throw new Error('Failed to start indexing due to an error');
    }
}

/**
 * Fetches all graphs for the current session.
 * @returns {Promise<Object>} - A promise that resolves to a list of jobs in dictionary format.
 */
export async function getIndexStatus() {
    getUserKey();
    const response = await fetch(`${API_BASE_URL}/index/status/`, {
        method: 'GET',
        credentials: 'include',
    }).then((response) => {
            if (!response.ok) {
                const error = response;
                console.error('Error fetching index status:', error);
                throw new Error('Network response was not ok');
            } 
            const responseJson = response.json();

            return responseJson;
    }).then((data) => {
        const jobs = data
        // Check if response is an array
        if (!Array.isArray(jobs)) {
            console.error('Expected an array of jobs, but got:', jobs);
            throw new Error('Invalid response format');
        }
        // Map response (serialized GraphModel data) to a format suitable for frontend display.
        const extracted_jobs = jobs.map((job) => {
            const extracted_job = {
                id: job.id,
                uuid: job.uuid,
                display_id: job.display_id,
                title: job.title,
                user_key: job.user_key,
                status: job.status,
                progress: job.progress,
                percent_complete: job.percent_complete,
                all_workflows: job.all_workflows,
                completed_workflows: job.completed_workflows,
                failed_workflows: job.failed_workflows,
                owner_username: job.owner_username,
                owner_display_name: job.owner_display_name,
                shared_with: job.shared_with || [],
                shared_with_display_names: job.shared_with_display_names || [],
                can_manage: job.can_manage,
                completion_model: job.completion_model,
            extraction_version: job.extraction_version || 'v1',
            workflow_version: job.workflow_version || job.extraction_version || 'v1',
                chat_model: job.chat_model,
                public_access: Boolean(job.public_access),
            };
            return extracted_job;
        });

        return extracted_jobs;
    }).catch((error) => {
        console.error('Error fetching index status:', error);
        throw new Error('Failed to fetch index status due to an error');
    });
    return response; 
}

export async function duplicateGraph(id) {
    const csrfToken = await getCsrfToken();
    const response = await fetch(`${API_BASE_URL}/index/duplicate/${id}/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
        },
        body: '{}',
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(body.error || `Failed to duplicate graph (${response.status}).`);
    }
    return body;
}

/**
 * Deletes a specific graph.
 * @param {*} id - The ID of the index to delete.
 */
export async function deleteIndex(id) {
    try {
        const csrfToken = await getCsrfToken();
        const response = await fetch(`${API_BASE_URL}/index/delete/${id}/`, {
            method: 'DELETE',
            credentials: 'include',
            headers: {
                'X-CSRFToken': csrfToken,
            },
        });

        if (!response.ok) {
            const error = await response.text();
            throw new Error(error);
        }
    } catch (error) {
        console.error('Error deleting index:', error);
        throw new Error('Failed to delete index due to an error');
    }
}

export async function updateGraphTitle(id, title) {
    try {
        const csrfToken = await getCsrfToken();
        const response = await fetch(`${API_BASE_URL}/index/graph/${id}/`, {
            method: 'PATCH',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': csrfToken,
            },
            body: JSON.stringify({ title }),
        });

        if (!response.ok) {
            const error = await response.text();
            throw new Error(error);
        }

        return await response.json();
    } catch (error) {
        console.error('Error updating graph title:', error);
        throw new Error('Failed to update graph title due to an error.');
    }
}

export async function getGraphPapers(graphUuid) {
    const response = await fetch(`${API_BASE_URL}/upload/graphs/${graphUuid}/papers/`, {
        method: 'GET',
        credentials: 'include',
    });
    if (!response.ok) {
        throw new Error('Failed to load graph papers.');
    }
    return await response.json();
}

export async function renameGraphPaper(fileId, name) {
    const csrfToken = await getCsrfToken();
    const response = await fetch(`${API_BASE_URL}/upload/papers/${fileId}/`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
        },
        body: JSON.stringify({ name }),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to rename paper.');
    }
    return await response.json();
}

export async function getGraphShares(graphUuid) {
    const response = await fetch(`${API_BASE_URL}/graphs/${graphUuid}/shares/`, {
        credentials: 'include',
    });
    if (!response.ok) throw new Error('Failed to load graph sharing.');
    return await response.json();
}

export async function shareGraphWithUser(graphUuid, username) {
    const csrfToken = await getCsrfToken();
    const response = await fetch(`${API_BASE_URL}/graphs/${graphUuid}/shares/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
        },
        body: JSON.stringify({ username }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.error || 'Failed to share graph.');
    return body;
}

export async function unshareGraphWithUser(graphUuid, username) {
    const csrfToken = await getCsrfToken();
    const response = await fetch(`${API_BASE_URL}/graphs/${graphUuid}/shares/`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
        },
        body: JSON.stringify({ username }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.error || 'Failed to remove sharing.');
    return body;
}

// Use the already-authorized graph's read-only container URL to load raw records.
export async function getGraphProvenance(id, graphUuid) {
    const response = await fetch(`${API_BASE_URL}/search/getfiles/${id}/`, { credentials: 'include' });
    if (!response.ok) throw new Error('Unable to retrieve source records.');
    const files = await response.json();
    const entityFile = files.find(file => file.file_name === 'entities.parquet');
    if (!entityFile) throw new Error('Source records are unavailable for this graph.');
    const url = new URL(entityFile.url);
    url.pathname = url.pathname.replace(/[^/]+$/, 'v2_provenance.json');
    const raw = await fetch(url.toString(), { mode: 'cors' });
    if (!raw.ok) throw new Error('Full source records are unavailable for this graph.');
    const provenance = await raw.json();
    if (graphUuid) {
        try {
            const papers = await getGraphPapers(graphUuid);
            provenance.paper_sources = Object.fromEntries(papers.filter(paper => paper.file_name && paper.asset_id).map(paper => [paper.file_name, { id: paper.id }]));
            provenance.paper_names = Object.fromEntries(papers.filter(paper => paper.file_name && paper.name).map(paper => [paper.file_name, paper.name]));
        } catch { /* Extraction provenance still works when the paper list is unavailable. */ }
    }
    return provenance;
}

export async function getStatisticalExportStatus(graphId) {
    getUserKey();
    const response = await fetch(`${API_BASE_URL}/index/statistical-results/${graphId}/`, { credentials: 'include' });
    if (!response.ok) throw new Error('Unable to read statistical export status.');
    return response.json();
}

export async function downloadStatisticalResults(graphId) {
    getUserKey();
    const response = await fetch(`${API_BASE_URL}/index/statistical-results/${graphId}/?download=1`, { credentials: 'include' });
    if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.detail || 'Statistical results are not available yet.');
    }
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement('a');
    link.href = url;
    link.download = `graph-${graphId}-statistical-results.xlsx`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function getStatisticalPreview(graphId, graphUuid) {
    getUserKey();
    const response = await fetch(`${API_BASE_URL}/index/statistical-preview/${graphId}/`, { credentials: 'include' });
    if (!response.ok) throw new Error('Statistical preview is not ready.');
    const result = await response.json();
    if (graphUuid) {
        const papers = await getGraphPapers(graphUuid).catch(() => []);
        result.paper_sources = Object.fromEntries(papers.filter(paper => paper.file_name && paper.asset_id).map(paper => [paper.file_name, { id: paper.id }]));
        result.paper_names = Object.fromEntries(papers.filter(paper => paper.file_name && paper.name).map(paper => [paper.file_name, paper.name]));
    }
    return result;
}
